import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import {
  ScrollIcon as ScrollFlat, House, Lightning, ShieldCheck, Notebook, CheckCircle, FloppyDisk,
  Sparkle, Warning, Stack, FilePdf, FileDoc, Download, Hourglass, ChartBar, ClockClockwise,
} from "@phosphor-icons/react";
import { RIESGO_PLANTILLAS, fillRiesgoTemplate, POLIZA_DECISIONES } from "./riesgoTemplates";
import { GestionesPendientesBlock, ReservaPreventivaBlock } from "./PreliminarBlocks";
import SectionCard from "./SectionCard";
import StatusChip from "./StatusChip";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { CaretDown } from "@phosphor-icons/react";
import { getToken } from "@/lib/authToken";
const SECCIONES = [
  {
    k: "informe_descripcion_riesgo",
    label: "Descripción del riesgo",
    Icon: House,
    placeholder: "Naturaleza, ubicación, características constructivas, uso del riesgo, antigüedad, instalaciones relevantes, etc.",
  },
  {
    k: "informe_causas_circunstancias",
    label: "Causas y circunstancias",
    Icon: Lightning,
    placeholder: "Cómo, dónde y cuándo ocurrió. Causa real determinada en la peritación, mecanismo del siniestro, elementos afectados.",
  },
  {
    k: "informe_aplicacion_poliza",
    label: "Aplicación de la póliza",
    Icon: ShieldCheck,
    placeholder: "Coberturas implicadas, garantías aplicables, exclusiones a invocar, franquicia aplicable, sumas aseguradas relevantes.",
  },
  {
    k: "informe_observaciones",
    label: "Observaciones",
    Icon: Notebook,
    placeholder: "Comentarios adicionales del perito: gestión con asegurado, incidencias durante la peritación, recomendaciones, etc.",
  },
];

const isEmpty = (v) => !v || !v.trim();

export default function InformeTab({ exp, onReload }) {
  const initial = SECCIONES.reduce((acc, s) => ({ ...acc, [s.k]: exp[s.k] || "" }), {});
  const [values, setValues] = useState(initial);
  const [savedFlash, setSavedFlash] = useState({});
  const debounceRefs = useRef({});

  // IA expand
  const [iaProposal, setIaProposal] = useState(null);   // {propuesta, notas_used}
  const [iaLoading, setIaLoading] = useState(false);
  const [iaError, setIaError] = useState("");

  // Árbol de decisión "Aplicación de la póliza" (Sí / No / Parcial)
  const [polizaDecision, setPolizaDecision] = useState(null); // 'si' | 'no' | 'parcial' | null

  // Notas de campo del expediente (para el botón IA)
  const notasCampo = (exp.observaciones_especiales || "").trim();

  // Resync si cambia el expediente desde fuera
  useEffect(() => {
    setValues(SECCIONES.reduce((acc, s) => ({ ...acc, [s.k]: exp[s.k] || "" }), {}));
  }, [exp.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const persist = async (key, value) => {
    try {
      await api.put(`/expedientes/${exp.id}`, { [key]: value });
      setSavedFlash((f) => ({ ...f, [key]: true }));
      setTimeout(() => setSavedFlash((f) => ({ ...f, [key]: false })), 1200);
      onReload?.();
    } catch (e) {
      // silencioso, on-blur re-intentará
    }
  };

  const onChange = (key, value) => {
    setValues((v) => ({ ...v, [key]: value }));
    // Debounce para auto-guardar mientras se escribe (1.2s)
    if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]);
    debounceRefs.current[key] = setTimeout(() => persist(key, value), 1200);
  };

  const onBlur = (key) => {
    if (debounceRefs.current[key]) {
      clearTimeout(debounceRefs.current[key]);
      debounceRefs.current[key] = null;
    }
    persist(key, values[key]);
  };

  const filledCount = SECCIONES.filter((s) => !isEmpty(values[s.k])).length;
  const totalChars = Object.values(values).reduce((a, v) => a + (v?.length || 0), 0);

  const generateCausas = async () => {
    if (!notasCampo) {
      setIaError("Primero añade notas de campo en la pestaña 'Expediente' (campo Notas de campo de perito).");
      return;
    }
    setIaLoading(true); setIaError(""); setIaProposal(null);
    try {
      const { data } = await api.post(`/expedientes/${exp.id}/informe/expand-causas`, {});
      setIaProposal(data);
    } catch (e) {
      setIaError(e?.response?.data?.detail || "Error generando texto con IA");
    } finally {
      setIaLoading(false);
    }
  };

  const acceptProposal = async (mode) => {
    // mode: 'replace' o 'append'
    if (!iaProposal) return;
    const current = values.informe_causas_circunstancias || "";
    const next = mode === "append" && current.trim()
      ? `${current.trim()}\n\n${iaProposal.propuesta}`
      : iaProposal.propuesta;
    setValues((v) => ({ ...v, informe_causas_circunstancias: next }));
    setIaProposal(null);
    await persist("informe_causas_circunstancias", next);
  };

  const applyRiesgoTemplate = async (template) => {
    const filled = fillRiesgoTemplate(template.text, exp);
    const current = (values.informe_descripcion_riesgo || "").trim();
    if (current) {
      const ok = confirm(
        `Ya hay texto en "Descripción del riesgo".\n\n` +
        `¿Reemplazarlo con la plantilla "${template.label}"?\n\n` +
        `(Cancela para mantener el texto actual.)`
      );
      if (!ok) return;
    }
    setValues((v) => ({ ...v, informe_descripcion_riesgo: filled }));
    await persist("informe_descripcion_riesgo", filled);
  };

  // Generador de Informe (PDF / Word)
  const [generating, setGenerating] = useState(null); // 'pdf' | 'docx' | null
  const [genError, setGenError] = useState("");
  const [genResult, setGenResult] = useState(null);   // {format, attachment_id, filename}

  const downloadAttachment = async (attId, filename) => {
    try {
      const tok = getToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/attachments/${attId}/file`,
        { headers: tok ? { Authorization: `Bearer ${tok}` } : {} },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "informe";
      document.body.appendChild(a); a.click();
      a.remove(); setTimeout(() => URL.revokeObjectURL(url), 800);
      return true;
    } catch {
      // No mostramos traza técnica. El informe ya está guardado en BD y disponible
      // en la pestaña Documentos — el caller decide cómo notificar.
      return false;
    }
  };

  const generateInforme = async (fmt, templateId = "generic") => {
    setGenerating(fmt); setGenError(""); setGenResult(null);
    try {
      const { data } = await api.post(
        `/expedientes/${exp.id}/informe/generate`,
        { format: fmt, template_id: templateId },
      );
      setGenResult(data);
      const downloaded = await downloadAttachment(data.attachment_id, data.filename);
      if (!downloaded) {
        setGenResult({ ...data, downloadFailed: true });
      }
    } catch (e) {
      setGenError(e?.response?.data?.detail || "Error generando el informe");
    } finally {
      setGenerating(null);
    }
  };

  // Generador de Informe PRELIMINAR (PDF / Word). Mantiene el mismo flujo de
  // descarga pero llama a otro endpoint y cambia el estado del expediente.
  const [generatingPrelim, setGeneratingPrelim] = useState(null);
  const generatePreliminar = async (fmt, templateId = "generic") => {
    setGeneratingPrelim(fmt); setGenError(""); setGenResult(null);
    try {
      const { data } = await api.post(
        `/expedientes/${exp.id}/informe-preliminar/generate`,
        { format: fmt, template_id: templateId },
      );
      const downloaded = await downloadAttachment(data.attachment_id, data.filename);
      setGenResult({ ...data, preliminar: true, downloadFailed: !downloaded });
      onReload?.();
    } catch (e) {
      setGenError(e?.response?.data?.detail || "Error generando el informe preliminar");
    } finally {
      setGeneratingPrelim(null);
    }
  };

  // Inspección de Comunidad — informe especializado en PDF que combina
  // Catastro + ortofoto IGN PNOA + Street View Mapillary + fotografías.
  const tipoEncargoLc = (
    exp?.extracted_data?.tipo_encargo
    || exp?.tipo_encargo
    || ""
  ).toLowerCase();
  const esInspeccionComunidad =
    tipoEncargoLc.includes("inspecci") && tipoEncargoLc.includes("comunidad");
  const [generatingInspComunidad, setGeneratingInspComunidad] = useState(false);
  const [inspPreviewPois, setInspPreviewPois] = useState(null);
  const [inspExcluded, setInspExcluded] = useState({});
  const openInspeccionPreview = async () => {
    setGeneratingInspComunidad(true); setGenError(""); setGenResult(null);
    try {
      const { data } = await api.get(
        `/expedientes/${exp.id}/informe/inspeccion-comunidad/preview-pois`,
      );
      setInspPreviewPois(data.pois || []);
      setInspExcluded({});  // todo incluido por defecto
    } catch (e) {
      setGenError(
        e?.response?.data?.detail
        || "Error obteniendo el listado de comercios",
      );
    } finally {
      setGeneratingInspComunidad(false);
    }
  };
  const generateInspeccionComunidad = async () => {
    setGeneratingInspComunidad(true); setGenError(""); setGenResult(null);
    try {
      const excluded_poi_names = Object.entries(inspExcluded || {})
        .filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post(
        `/expedientes/${exp.id}/informe/inspeccion-comunidad/generate-pdf`,
        { excluded_poi_names },
      );
      const downloaded = await downloadAttachment(data.id, data.filename);
      setGenResult({
        format: "pdf", attachment_id: data.id, filename: data.filename,
        inspeccionComunidad: true, downloadFailed: !downloaded,
      });
      setInspPreviewPois(null);
      onReload?.();
    } catch (e) {
      setGenError(
        e?.response?.data?.detail
        || "Error generando el informe de inspección de comunidad",
      );
    } finally {
      setGeneratingInspComunidad(false);
    }
  };

  // Lista de plantillas custom disponibles para este expediente (Mapfre·Gefoex
  // sólo aparece si el gabinete del expediente es Gefoex). Se carga una sola
  // vez al montar el componente.
  const [customTemplates, setCustomTemplates] = useState([]);
  useEffect(() => {
    if (!exp?.id) return;
    let cancelled = false;
    api.get(`/expedientes/${exp.id}/informe-templates`).then(({ data }) => {
      if (cancelled) return;
      setCustomTemplates((data?.templates || []).filter((t) => t.is_custom));
    }).catch(() => { if (!cancelled) setCustomTemplates([]); });
    return () => { cancelled = true; };
  }, [exp?.id]);

  // Restaurar informe final (Ctrl+Z) — solo aplicable si exp.fecha_cierre.
  // Llama al endpoint /unfinalize que borra PDF, minuta, importe_minuta_manual
  // y vuelve al estado previo. La tasación se mantiene editable.
  const [unfinalizing, setUnfinalizing] = useState(false);
  const restoreFinal = async () => {
    const min = exp.importe_minuta ? ` (minuta: ${Number(exp.importe_minuta).toFixed(2)} €)` : "";
    if (!confirm(
      `¿Restaurar el informe final?${min}\n\n` +
      `· Se borrará el PDF del informe final\n` +
      `· Se borrará la minuta calculada\n` +
      `· El expediente volverá al estado anterior\n\n` +
      `Tasación e importes editables se mantienen para que puedas corregirlos.`
    )) return;
    setUnfinalizing(true);
    try {
      await api.post(`/expedientes/${exp.id}/unfinalize`);
      onReload?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "No se ha podido restaurar el informe");
    } finally {
      setUnfinalizing(false);
    }
  };

  const applyPolizaDecision = async (text) => {
    const filled = fillRiesgoTemplate(text, exp);
    const current = (values.informe_aplicacion_poliza || "").trim();
    if (current) {
      const ok = confirm(
        `Ya hay texto en "Aplicación de la póliza".\n\n` +
        `¿Reemplazarlo con la decisión seleccionada?\n\n` +
        `(Cancela para mantener el texto actual.)`
      );
      if (!ok) return;
    }
    setValues((v) => ({ ...v, informe_aplicacion_poliza: filled }));
    await persist("informe_aplicacion_poliza", filled);
  };

  // Atajo de teclado Ctrl+Z (Cmd+Z en Mac) → restaurar informe final si el
  // expediente está cerrado. No interfiere con el undo nativo de campos
  // editables porque solo se activa cuando el foco NO está en un input o
  // textarea (evitamos pisar el undo de texto del usuario).
  useEffect(() => {
    if (!exp.fecha_cierre) return;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key !== "z" && e.key !== "Z") return;
      const tag = (e.target?.tagName || "").toUpperCase();
      const editable = tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable;
      if (editable) return;
      e.preventDefault();
      restoreFinal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [exp.fecha_cierre, exp.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-full overflow-y-auto" data-testid="informe-tab">
      {exp.estado_encargo === "preliminar" && (
        <div
          className="px-3 md:px-6 py-1 bg-status-warning-bg/50 text-status-warning-fg text-[11px] inline-flex items-center gap-1.5 w-full"
          data-testid="informe-prelim-banner"
        >
          <Hourglass className="w-3 h-3 flex-shrink-0" weight="regular" />
          <span className="truncate">
            Estado preliminar — al generar el informe final pasará a cierre normal.
          </span>
        </div>
      )}

      <div className="px-3 md:px-6 py-4 md:py-5 flex flex-col lg:flex-row gap-5 items-start">
        {/* ===== Columna principal: secciones del informe ===== */}
        <div className="flex-1 min-w-0 space-y-5">
          {/* Bloques del informe preliminar — tarjetas hermanas del resto,
              marcadas con pill "preliminar" + borde lateral izquierdo azul. */}
          <GestionesPendientesBlock exp={exp} onReload={onReload} />
          <ReservaPreventivaBlock exp={exp} onReload={onReload} />

          {/* Apartados del informe — cada uno en su propia SectionCard,
              con el MISMO patrón visual que las tarjetas de Expediente. */}
          {SECCIONES.map((s) => {
          const filled = !isEmpty(values[s.k]);
          const flashed = savedFlash[s.k];
          return (
            <SectionCard
              key={s.k}
              icon={s.Icon}
              tone={filled ? "teal" : "gray"}
              title={s.label}
              testid={`informe-seccion-${s.k}`}
              meta={
                <>
                  {s.k === "informe_descripcion_riesgo" && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          data-testid="riesgo-templates-trigger"
                          className="inline-flex items-center gap-1 px-2 h-6 rounded-md border border-brand/30 bg-white text-brand text-[11px] font-semibold hover:bg-brand-soft hover:border-brand transition-colors"
                        >
                          <Stack className="w-3 h-3" weight="regular" />
                          Plantilla
                          <CaretDown className="w-3 h-3" weight="bold" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="w-64 !bg-white !text-foreground border border-border shadow-lg"
                      >
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted font-semibold">
                          Plantillas de riesgo
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {RIESGO_PLANTILLAS.map((tpl) => (
                          <DropdownMenuItem
                            key={tpl.id}
                            onSelect={() => applyRiesgoTemplate(tpl)}
                            data-testid={`riesgo-tpl-${tpl.id}`}
                            className="cursor-pointer hover:bg-brand-soft focus:bg-brand-soft focus:text-foreground"
                          >
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[12.5px] font-medium text-foreground">{tpl.label}</span>
                              {tpl.description && (
                                <span className="text-[10.5px] text-muted line-clamp-2">{tpl.description}</span>
                              )}
                            </div>
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <div className="px-2 py-1.5 text-[10px] text-muted normal-case tracking-normal leading-snug">
                          Rellenan municipio y provincia del expediente.
                          Los <span className="font-mono font-bold text-foreground">***</span> son
                          campos a completar a mano.
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <StatusChip filled={filled} testid={`informe-status-${s.k}`} />
                  <span className="tabular-nums text-[11px]">
                    {(values[s.k] || "").length.toLocaleString("es-ES")} car.
                  </span>
                  {flashed && (
                    <span className="inline-flex items-center gap-1 text-status-success-fg font-semibold uppercase tracking-wider text-[10px]">
                      <FloppyDisk className="w-3 h-3" weight="fill" /> guardado
                    </span>
                  )}
                </>
              }
            >
                {s.k === "informe_aplicacion_poliza" && (
                  <div className="mb-3 pb-3 border-b border-dashed border-border" data-testid="poliza-decision-tree">
                    {/* Pregunta + segmented toggle en una sola línea, estilo
                        idéntico al selector "Estado del encargo" del DataTab. */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-2">
                      <div className="text-[12px] font-medium text-muted flex items-center gap-1.5">
                        <Stack className="w-3.5 h-3.5" weight="regular" />
                        ¿El siniestro tiene cobertura?
                      </div>
                      <div
                        role="tablist"
                        aria-label="Aplicación de la póliza"
                        className="inline-flex items-stretch rounded-lg border border-border bg-surface-input overflow-hidden p-0.5 gap-0.5"
                        data-testid="poliza-decision-toggle"
                      >
                        {POLIZA_DECISIONES.map((dec) => {
                          const active = polizaDecision === dec.id;
                          const activeTone = {
                            success: "text-status-success-fg",
                            danger:  "text-status-error-fg",
                            warning: "text-status-warning-fg",
                          }[dec.tone] || "text-brand";
                          return (
                            <button
                              key={dec.id}
                              role="tab"
                              aria-selected={active}
                              type="button"
                              onClick={() => {
                                setPolizaDecision(dec.id);
                                if (!dec.opciones && dec.text) {
                                  applyPolizaDecision(dec.text);
                                }
                              }}
                              data-testid={`poliza-dec-${dec.id}`}
                              title={dec.description}
                              className={`text-[12.5px] px-3 h-7 rounded-md transition-all ${
                                active
                                  ? `bg-white ${activeTone} font-semibold shadow-sm ring-1 ring-border`
                                  : "bg-transparent text-muted hover:text-foreground hover:bg-white/60 font-medium"
                              }`}
                            >
                              {dec.short || dec.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Nivel 2: opciones de la decisión activa (solo Sí y Parcial) */}
                    {polizaDecision && (() => {
                      const dec = POLIZA_DECISIONES.find((d) => d.id === polizaDecision);
                      if (!dec || !dec.opciones) return null;
                      return (
                        <div className="rounded-md border border-border bg-background px-3 py-3" data-testid={`poliza-opciones-${polizaDecision}`}>
                          <div className="text-[12px] font-medium text-muted mb-2">
                            Tipo de gestión
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {dec.opciones.map((op) => (
                              <button
                                key={op.id}
                                type="button"
                                onClick={() => applyPolizaDecision(op.text)}
                                data-testid={`poliza-op-${op.id}`}
                                className="text-[11.5px] inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-brand/30 bg-white text-brand font-medium hover:bg-brand-soft hover:border-brand transition-colors"
                              >
                                {op.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    <div className="text-[10.5px] text-muted mt-2 normal-case tracking-normal">
                      La <span className="font-semibold">causa</span> del expediente se inserta automáticamente
                      en el texto. Los <span className="font-mono font-bold text-foreground">***</span> restantes
                      son campos a completar a mano (importes, etc.).
                    </div>
                  </div>
                )}

                <textarea
                  rows={6}
                  value={values[s.k]}
                  onChange={(e) => onChange(s.k, e.target.value)}
                  onBlur={() => onBlur(s.k)}
                  data-testid={`informe-textarea-${s.k}`}
                  placeholder={s.placeholder}
                  className="no-base-input !text-[13px] !leading-relaxed !min-h-[140px] !border !border-border !shadow-none !bg-white !w-full !rounded-md focus:!ring-2 focus:!ring-brand/20 focus:!border-brand !p-2.5"
                />

                {s.k === "informe_causas_circunstancias" && (
                  <div className="mt-3 pt-3 border-t border-border/60" data-testid="ia-causas-block">
                    {!iaProposal && (
                      <div className="flex items-start gap-3 flex-wrap">
                        <button
                          onClick={generateCausas}
                          disabled={iaLoading || !notasCampo}
                          data-testid="ia-causas-generate"
                          className={`text-[11.5px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            iaLoading
                              ? "bg-brand-soft text-brand"
                              : "bg-gradient-to-r from-brand to-brand-hover text-white hover:opacity-90"
                          }`}
                        >
                          <Sparkle className="w-3.5 h-3.5" weight="fill" />
                          {iaLoading ? "Generando con IA..." : "Desarrollar con IA desde notas de campo"}
                        </button>
                        <div className="text-[11px] text-muted flex items-center gap-1.5">
                          {notasCampo ? (
                            <>
                              <CheckCircle className="w-3 h-3 text-status-success-fg" weight="fill" />
                              {notasCampo.length} caracteres de notas listas para expandir
                            </>
                          ) : (
                            <>
                              <Warning className="w-3 h-3 text-status-warning-fg" weight="fill" />
                              Añade notas de campo en la pestaña Expediente para activar la IA
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {iaError && (
                      <div className="mt-2 text-[11.5px] text-status-error-fg flex items-start gap-1.5">
                        <Warning className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" weight="bold" />
                        <span>{iaError}</span>
                      </div>
                    )}

                    {iaProposal && (
                      <div data-testid="ia-causas-proposal" className="rounded-md border-2 border-brand/40 bg-brand-soft/30 p-3">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-brand mb-2">
                          <Sparkle className="w-3.5 h-3.5" weight="fill" />
                          Propuesta de la IA
                        </div>
                        <div className="text-[12.5px] text-foreground whitespace-pre-wrap leading-relaxed">
                          {iaProposal.propuesta}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-brand/30">
                          <button
                            onClick={() => acceptProposal("replace")}
                            data-testid="ia-causas-accept-replace"
                            className="text-[11px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand text-white font-semibold hover:bg-brand-hover"
                          >
                            <CheckCircle className="w-3.5 h-3.5" weight="bold" />
                            {values[s.k]?.trim() ? "Reemplazar texto actual" : "Usar este texto"}
                          </button>
                          {values[s.k]?.trim() && (
                            <button
                              onClick={() => acceptProposal("append")}
                              data-testid="ia-causas-accept-append"
                              className="text-[11px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-brand/40 bg-white text-brand font-semibold hover:bg-brand-soft"
                            >
                              Añadir al final
                            </button>
                          )}
                          <button
                            onClick={generateCausas}
                            disabled={iaLoading}
                            data-testid="ia-causas-regenerate"
                            className="text-[11px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-white text-muted font-medium hover:text-foreground"
                          >
                            <Sparkle className="w-3 h-3" weight="bold" />
                            Regenerar
                          </button>
                          <button
                            onClick={() => setIaProposal(null)}
                            data-testid="ia-causas-discard"
                            className="text-[11px] px-3 py-1.5 rounded-md text-muted hover:text-status-error-fg font-medium"
                          >
                            Descartar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </SectionCard>
          );
        })}

          <div className="help-text italic">
            Guardado automático al pausar de escribir. Se sincroniza al PDF del informe final cada vez
            que cierras el siniestro.
          </div>
        </div>

        {/* ===== Columna lateral sticky (Progreso + Generación) ===== */}
        <InformeSidebar
          exp={exp}
          values={values}
          generating={generating}
          generatingPrelim={generatingPrelim}
          genResult={genResult}
          genError={genError}
          onGenerate={generateInforme}
          onGeneratePrelim={generatePreliminar}
          customTemplates={customTemplates}
          onRedownload={() => downloadAttachment(genResult.attachment_id, genResult.filename)}
          onRestoreFinal={restoreFinal}
          unfinalizing={unfinalizing}
          esInspeccionComunidad={esInspeccionComunidad}
          generatingInspComunidad={generatingInspComunidad}
          onGenerateInspComunidad={openInspeccionPreview}
        />
      </div>
      {/* Modal selector de comercios para el informe Inspección Comunidad */}
      {inspPreviewPois && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="insp-com-modal"
          onClick={() => !generatingInspComunidad && setInspPreviewPois(null)}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-border">
              <h3 className="font-display font-bold text-base text-foreground">
                Comercios identificados ({inspPreviewPois.length})
              </h3>
              <p className="text-[11.5px] text-muted leading-snug mt-1">
                Desmarca los comercios que <strong>NO</strong> deban incluirse en el informe
                (mal referenciados en Google Maps, ya cerrados, etc.).
              </p>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-2">
              {inspPreviewPois.length === 0 && (
                <div className="text-sm text-muted py-6 text-center">
                  No se han encontrado comercios para esta dirección.
                </div>
              )}
              {inspPreviewPois.map((p) => {
                const excluded = !!inspExcluded[p.name];
                return (
                  <label
                    key={p.name}
                    className={`flex items-start gap-3 py-2 border-b border-border/60 cursor-pointer ${excluded ? "opacity-50" : ""}`}
                    data-testid={`insp-com-poi-row-${p.name}`}
                  >
                    <input
                      type="checkbox"
                      checked={!excluded}
                      onChange={(e) =>
                        setInspExcluded((prev) => ({ ...prev, [p.name]: !e.target.checked }))
                      }
                      className="mt-1 w-4 h-4"
                      data-testid={`insp-com-poi-check-${p.name}`}
                    />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold text-foreground">{p.name}</div>
                      <div className="text-[11px] text-muted">
                        {p.category_label}
                        {p.address ? ` · ${p.address}` : ""}
                        {p.level ? ` · Planta ${p.level}` : ""}
                        {p.phone ? ` · ${p.phone}` : ""}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="px-5 py-3 border-t border-border flex items-center justify-between">
              <span className="text-[11px] text-muted">
                {inspPreviewPois.length - Object.values(inspExcluded || {}).filter(Boolean).length} de {inspPreviewPois.length} incluidos
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setInspPreviewPois(null)}
                  disabled={generatingInspComunidad}
                  className="text-[12px] px-3 h-9 rounded-md border border-border bg-white hover:bg-muted/10 disabled:opacity-50"
                  data-testid="insp-com-cancel"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={generateInspeccionComunidad}
                  disabled={generatingInspComunidad}
                  className="inline-flex items-center gap-1.5 text-[12px] px-3 h-9 rounded-md font-semibold bg-foreground text-white hover:bg-foreground/90 disabled:opacity-50"
                  data-testid="insp-com-confirm"
                >
                  <FilePdf className="w-4 h-4" weight="regular" />
                  {generatingInspComunidad ? "Generando…" : "Generar PDF"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================================================================
 * InformeSidebar — Columna lateral sticky (~310px) de la pestaña Informe.
 * Contiene dos tarjetas:
 *   1. Progreso del informe — anillo circular (apartados completos / total)
 *      + índice de los 6 bloques del informe con estado (check verde o
 *      número) y dato breve (caracteres, importe, "prelim."). Cada item
 *      hace scroll suave hasta su SectionCard.
 *   2. Generación del informe (PDF/Word final + PDF/Word preliminar +
 *      último archivo generado + errores).
 * En pantallas estrechas (< lg) se apila debajo de la columna principal.
 * ==================================================================== */
const fmtEUR0 = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

function scrollToTestId(testid) {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function ProgressRing({ completed, total }) {
  const size = 76;
  const stroke = 7;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? completed / total : 0;
  const offset = c * (1 - pct);
  return (
    <div className="relative" style={{ width: size, height: size }} aria-hidden>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className="stroke-background"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="stroke-status-success-fg transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-[15px] font-bold tabular-nums text-foreground leading-none">
          {completed}<span className="text-muted/70">/{total}</span>
        </div>
        <div className="text-[9px] uppercase tracking-widest font-semibold text-muted mt-0.5">
          apartados
        </div>
      </div>
    </div>
  );
}

function ProgressIndexItem({ idx, item }) {
  const { label, brief, completed, prelim, testid } = item;
  return (
    <button
      type="button"
      onClick={() => scrollToTestId(testid)}
      data-testid={`informe-progress-item-${item.key}`}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-background transition-colors group"
    >
      {/* Estado: check verde o número */}
      <span className="flex-shrink-0 w-5 h-5 inline-flex items-center justify-center">
        {completed ? (
          <CheckCircle className="w-5 h-5 text-status-success-fg" weight="fill" />
        ) : (
          <span className="w-5 h-5 rounded-full border border-border bg-white text-[10.5px] font-bold text-muted inline-flex items-center justify-center tabular-nums">
            {idx}
          </span>
        )}
      </span>

      {/* Etiqueta */}
      <span className={`flex-1 min-w-0 text-[12px] truncate ${completed ? "text-foreground font-medium" : "text-muted"} group-hover:text-foreground`}>
        {label}
      </span>

      {/* Dato breve a la derecha */}
      <span className="flex-shrink-0 flex items-center gap-1">
        {prelim && (
          <span className="text-[9px] uppercase tracking-wider font-semibold text-accent-purple-fg bg-accent-purple-bg/60 px-1 py-px rounded">
            prelim.
          </span>
        )}
        <span className={`text-[10.5px] tabular-nums ${completed ? "text-foreground" : "text-muted/70"}`}>
          {brief}
        </span>
      </span>
    </button>
  );
}

function InformeSidebar({
  exp, values,
  generating, generatingPrelim,
  genResult, genError,
  onGenerate, onGeneratePrelim, onRedownload,
  onRestoreFinal, unfinalizing,
  customTemplates = [],
  esInspeccionComunidad = false,
  generatingInspComunidad = false,
  onGenerateInspComunidad,
}) {
  const busy = generating !== null || generatingPrelim !== null || unfinalizing || generatingInspComunidad;

  // Recomendación contextual: si aún no se ha emitido IP → la acción
  // recomendada es el preliminar. Una vez emitido (y mientras no se
  // cierre), la recomendada pasa a ser el informe final.
  const prelimRecommended = !exp.fecha_preliminar;
  const finalRecommended = !!exp.fecha_preliminar && !exp.fecha_cierre;
  // El expediente ya está cerrado — mostramos un panel "Cierre" que sustituye
  // los botones de generación por la información del cierre + un botón
  // prominente "Restaurar informe final (Ctrl+Z)" que llama al endpoint
  // /unfinalize. La tasación queda editable; la minuta se borra al restaurar.
  const isClosed = !!exp.fecha_cierre;

  // Construir el índice de bloques con estado y dato breve. Los dos
  // primeros bloques (Gestiones pendientes y Reserva preventiva) son
  // exclusivos del informe preliminar y se marcan con la pill "prelim.".
  const gestionesTxt = (exp.gestiones_pendientes || "").trim();
  const reservaItems = exp.reserva_preventiva_garantias || [];
  const reservaTotal = reservaItems.reduce((acc, it) => acc + Number(it.importe || 0), 0);

  const items = [
    {
      key: "gestiones",
      label: "Gestiones pendientes",
      testid: "informe-gestiones-pendientes",
      completed: gestionesTxt.length > 0,
      brief: gestionesTxt.length > 0 ? `${gestionesTxt.length} car.` : "—",
      prelim: true,
    },
    {
      key: "reserva",
      label: "Reserva preventiva",
      testid: "informe-reserva-preventiva",
      completed: reservaItems.length > 0,
      brief: reservaItems.length > 0 ? fmtEUR0(reservaTotal) : "—",
      prelim: true,
    },
    ...SECCIONES.map((s) => {
      const v = (values[s.k] || "");
      const trimmed = v.trim();
      return {
        key: s.k,
        label: s.label,
        testid: `informe-seccion-${s.k}`,
        completed: trimmed.length > 0,
        brief: trimmed.length > 0 ? `${v.length} car.` : "—",
        prelim: false,
      };
    }),
  ];

  const completedCount = items.filter((i) => i.completed).length;
  const totalCount = items.length;

  return (
    <aside
      data-testid="informe-sidebar"
      className="w-full lg:w-[310px] lg:flex-shrink-0 space-y-5 lg:sticky lg:top-0 lg:self-start"
    >
      {/* ---- Tarjeta 1: Progreso del informe ---- */}
      <section
        className="bg-white border border-border rounded-lg overflow-hidden"
        data-testid="informe-sidebar-progress"
      >
        <header className="flex items-center gap-2 px-4 py-2.5 border-b border-border/70">
          <span className="icon-chip chip-icon-teal flex-shrink-0">
            <ChartBar className="w-4 h-4" weight="regular" />
          </span>
          <h3 className="section-title">Progreso del informe</h3>
        </header>

        {/* Anillo circular */}
        <div className="px-4 pt-4 pb-3 flex items-center justify-center">
          <ProgressRing completed={completedCount} total={totalCount} />
        </div>

        {/* Índice de bloques */}
        <ul
          className="px-2 pb-3 space-y-0.5 border-t border-border/40 pt-2"
          data-testid="informe-progress-index"
        >
          {items.map((item, i) => (
            <li key={item.key}>
              <ProgressIndexItem idx={i + 1} item={item} />
            </li>
          ))}
        </ul>
      </section>

      {/* ---- Tarjeta Cierre (solo si el expediente ya está finalizado) ----
          Sustituye al CTA de "Generar PDF final" y permite restaurar el
          informe final con un "Ctrl+Z" prominente. ---- */}
      {isClosed && (
        <section
          className="bg-white border border-status-success-bd rounded-lg overflow-hidden"
          data-testid="informe-sidebar-cierre"
        >
          <header className="flex items-center gap-2 px-4 py-2.5 border-b border-status-success-bd/60 bg-status-success-bg/40">
            <span className="icon-chip flex-shrink-0 bg-status-success-bg text-status-success-fg">
              <CheckCircle className="w-4 h-4" weight="fill" />
            </span>
            <h3 className="section-title">Siniestro cerrado</h3>
          </header>
          <div className="px-4 py-3 space-y-2.5 text-[12px]">
            <div className="flex items-baseline justify-between">
              <span className="text-muted">Fecha cierre</span>
              <span className="font-semibold tabular-nums text-foreground">
                {(() => {
                  try {
                    const d = new Date(exp.fecha_cierre);
                    return isNaN(d) ? exp.fecha_cierre : d.toLocaleDateString("es-ES");
                  } catch { return exp.fecha_cierre; }
                })()}
              </span>
            </div>
            {exp.importe_tasacion != null && (
              <div className="flex items-baseline justify-between">
                <span className="text-muted">Tasación</span>
                <span className="font-mono tabular-nums text-foreground">
                  {Number(exp.importe_tasacion).toFixed(2)} €
                </span>
              </div>
            )}
            {exp.importe_minuta != null && (
              <div className="flex items-baseline justify-between">
                <span className="text-muted">Minuta</span>
                <span className="font-mono tabular-nums text-foreground">
                  {Number(exp.importe_minuta).toFixed(2)} €
                </span>
              </div>
            )}

            <button
              onClick={onRestoreFinal}
              disabled={unfinalizing}
              data-testid="informe-restore-final"
              title="Restaurar informe final (Ctrl+Z): borra el PDF y la minuta, y reabre el expediente"
              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-md border border-status-warning-bd bg-status-warning-bg/40 text-status-warning-fg text-[12.5px] font-semibold hover:bg-status-warning-bg transition-colors disabled:opacity-50"
            >
              <ClockClockwise className="w-4 h-4" weight="bold" />
              <span>{unfinalizing ? "Restaurando…" : "Restaurar informe final"}</span>
              <kbd className="ml-1 text-[9.5px] font-mono font-bold bg-white/80 text-status-warning-fg border border-status-warning-bd/60 px-1 py-px rounded">
                Ctrl+Z
              </kbd>
            </button>
            <p className="text-[10.5px] text-muted leading-snug">
              Borra el PDF del informe final y la minuta calculada. El expediente vuelve al estado anterior y la tasación queda editable.
            </p>
          </div>
        </section>
      )}

      {/* ---- Tarjeta 2: Generar ---- */}
      <section
        className="bg-white border border-border rounded-lg overflow-hidden"
        data-testid="informe-sidebar-generate"
      >
        <header className="flex items-center gap-2 px-4 py-2.5 border-b border-border/70">
          <span className="icon-chip chip-icon-amber flex-shrink-0">
            <Download className="w-4 h-4" weight="regular" />
          </span>
          <h3 className="section-title">Generar</h3>
        </header>
        <div className="px-4 py-3 space-y-4">
          {/* ── Preliminar (siempre arriba) ─────────────────────────── */}
          <div data-testid="informe-genblock-preliminar">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-status-info-bg text-status-info-fg">
                preliminar
              </span>
              {prelimRecommended && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider text-muted"
                  data-testid="recommended-now-preliminar"
                >
                  Recomendado ahora
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => onGeneratePrelim("pdf")}
                disabled={busy}
                data-testid="informe-prelim-pdf"
                className={`text-[12px] inline-flex items-center justify-center gap-1.5 px-2 h-9 rounded-md font-semibold transition-colors disabled:opacity-50 ${
                  prelimRecommended
                    ? "bg-brand text-white hover:bg-brand-hover border border-brand"
                    : "bg-white text-foreground border border-border hover:bg-background"
                }`}
              >
                <FilePdf className="w-4 h-4" weight="regular" />
                {generatingPrelim === "pdf" ? "…" : "PDF prelim."}
              </button>
              <button
                onClick={() => onGeneratePrelim("docx")}
                disabled={busy}
                data-testid="informe-prelim-docx"
                className="text-[12px] inline-flex items-center justify-center gap-1.5 px-2 h-9 rounded-md border border-border bg-white text-foreground font-semibold hover:bg-background disabled:opacity-50"
              >
                <FileDoc className="w-4 h-4" weight="regular" />
                {generatingPrelim === "docx" ? "…" : "Word prelim."}
              </button>
            </div>
            {customTemplates.length > 0 && (
              <div className="mt-2 space-y-1">
                {customTemplates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => onGeneratePrelim("docx", t.id)}
                    disabled={busy}
                    data-testid={`informe-prelim-template-${t.id}`}
                    title={`Generar PRELIMINAR con plantilla ${t.label}`}
                    className="w-full text-[11.5px] inline-flex items-center justify-center gap-1.5 px-2 h-8 rounded-md border border-sky-200 bg-sky-50 text-sky-800 font-semibold hover:bg-sky-100 disabled:opacity-50"
                  >
                    <FileDoc className="w-3.5 h-3.5" weight="regular" />
                    {generatingPrelim === "docx" ? "…" : t.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Informe final ──────────────────────────────────────── */}
          <div data-testid="informe-genblock-final">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted">
                Informe final
              </span>
              {finalRecommended && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider text-muted"
                  data-testid="recommended-now-final"
                >
                  · Recomendado ahora
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => onGenerate("pdf")}
                disabled={busy}
                data-testid="informe-generate-pdf"
                className={`text-[12px] inline-flex items-center justify-center gap-1.5 px-2 h-9 rounded-md font-semibold transition-colors disabled:opacity-50 ${
                  finalRecommended
                    ? "bg-brand text-white hover:bg-brand-hover border border-brand"
                    : "bg-white text-foreground border border-border hover:bg-background"
                }`}
              >
                <FilePdf className="w-4 h-4" weight="regular" />
                {generating === "pdf" ? "…" : "PDF final"}
              </button>
              <button
                onClick={() => onGenerate("docx")}
                disabled={busy}
                data-testid="informe-generate-docx"
                className="text-[12px] inline-flex items-center justify-center gap-1.5 px-2 h-9 rounded-md border border-border bg-white text-foreground font-semibold hover:bg-background disabled:opacity-50"
              >
                <FileDoc className="w-4 h-4" weight="regular" />
                {generating === "docx" ? "…" : "Word final"}
              </button>
            </div>
            {customTemplates.length > 0 && (
              <div className="mt-2 space-y-1">
                {customTemplates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => onGenerate("docx", t.id)}
                    disabled={busy}
                    data-testid={`informe-final-template-${t.id}`}
                    title={`Generar INFORME FINAL con plantilla ${t.label}`}
                    className="w-full text-[11.5px] inline-flex items-center justify-center gap-1.5 px-2 h-8 rounded-md border border-sky-200 bg-sky-50 text-sky-800 font-semibold hover:bg-sky-100 disabled:opacity-50"
                  >
                    <FileDoc className="w-3.5 h-3.5" weight="regular" />
                    {generating === "docx" ? "…" : t.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Inspección de Comunidad (sólo si el tipo de encargo coincide) ── */}
          {esInspeccionComunidad && (
            <div
              className="pt-2 border-t border-border/60"
              data-testid="informe-genblock-inspeccion-comunidad"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted">
                  Inspección de Comunidad
                </span>
                <span className="text-[9px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full font-semibold">
                  Nuevo
                </span>
              </div>
              <p className="text-[10.5px] text-muted leading-snug mb-2">
                Genera un informe PDF moderno con foto satélite (PNOA),
                Street View, ficha catastral del edificio, locales
                comerciales y fotografías de la inspección.
              </p>
              <button
                onClick={onGenerateInspComunidad}
                disabled={busy}
                data-testid="informe-inspeccion-comunidad-pdf"
                className="w-full text-[12px] inline-flex items-center justify-center gap-1.5 px-2 h-9 rounded-md font-semibold transition-colors disabled:opacity-50 bg-foreground text-white hover:bg-foreground/90 border border-foreground"
              >
                <FilePdf className="w-4 h-4" weight="regular" />
                {generatingInspComunidad
                  ? "Generando informe…"
                  : "Generar PDF Inspección Comunidad"}
              </button>
            </div>
          )}

          {/* ── Nota explicativa ──────────────────────────────────── */}
          <div className="text-[11px] text-muted leading-snug pt-1">
            Generar el final cierra el siniestro automáticamente.
          </div>

          {/* Feedback: último archivo generado */}
          {genResult && (
            <div className="pt-2 border-t border-border/60 space-y-1.5" data-testid="informe-genresult">
              <div className={`text-[11px] flex items-start gap-1.5 ${genResult.preliminar ? "text-status-info-fg" : "text-status-success-fg"}`}>
                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" weight="fill" />
                <span className="truncate" title={genResult.filename}>
                  {genResult.preliminar ? "Prelim · " : ""}{genResult.filename}
                </span>
              </div>
              <button
                onClick={onRedownload}
                data-testid="informe-redownload"
                className="text-[11px] inline-flex items-center gap-1 px-2 h-7 rounded-md border border-border bg-white text-muted hover:text-foreground hover:bg-background w-full justify-center"
              >
                <Download className="w-3 h-3" weight="regular" />
                Re-descargar
              </button>
            </div>
          )}

          {/* Error */}
          {genError && (
            <div
              className="pt-2 border-t border-border/60 text-[11px] text-status-error-fg flex items-start gap-1.5"
              data-testid="informe-generror"
            >
              <Warning className="w-3.5 h-3.5 flex-shrink-0 mt-px" weight="bold" />
              <span>{genError}</span>
            </div>
          )}
        </div>
      </section>
    </aside>
  );
}
